import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Grid, Link } from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  HourglassBottom as MissingIcon,
  Insights as AverageIcon,
  BeachAccess as DaysOffIcon,
} from '@mui/icons-material';
import { UserContext } from '../components/MiniDrawer';
import { ENABLE_COMPACT_SHELL, ENABLE_DAILY_TRACKING_V2, ENABLE_MONTHLY_PM_VIEW_V2, SUPPORT_CONFIG } from '../config/local.config';
import { useHomeMonth, useTeamMonth } from '../hooks/useHomeMonth';
import MonthPicker from '../components/monthly/MonthPicker';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { formatHours, MONTH_NAMES } from '../components/monthly/format';
import HomeHero from '../components/home/HomeHero';
import StatCard from '../components/home/StatCard';
import DailyHoursChart from '../components/home/DailyHoursChart';
import MonthCalendar from '../components/home/MonthCalendar';
import TimeBreakdown from '../components/home/TimeBreakdown';
import TeamPulse from '../components/home/TeamPulse';
import { DAY_COLORS, GREEN, MUTED } from '../components/home/homeTheme';
import { ClassicDesignNote } from '../components/home/NewDesignInvite';

const GUIDES = [
  { text: 'My Area guide', url: '/guide/RUNTIME-How to_ User Area.pdf' },
  { text: 'Manager Area guide', url: '/guide/RUNTIME-How to_ Manager Area.pdf' },
];

function greeting(date) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function HomeV2() {
  const { selectedUser, cognitoUser, backendUser } = useContext(UserContext);
  const navigate = useNavigate();
  const today = new Date();
  const [period, setPeriod] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });

  const stats = useHomeMonth(selectedUser, period.year, period.month);
  const isPm = Number(backendUser?.org_role_id) === 2;
  const team = useTeamMonth(backendUser?.id, isPm, period.year, period.month);

  const monthIndex = period.year * 12 + period.month;
  const currentIndex = today.getFullYear() * 12 + today.getMonth() + 1;
  const when = monthIndex < currentIndex ? 'past' : monthIndex > currentIndex ? 'future' : 'current';
  const monthName = `${MONTH_NAMES[period.month - 1]} ${period.year}`;
  const firstName = (backendUser?.name || cognitoUser?.name || '').trim().split(/\s+/)[0];

  const openDay = (dateKey) => navigate(`${ENABLE_DAILY_TRACKING_V2 ? '/daily-tracking-v2' : '/daily-tracking'}?date=${dateKey}`);
  const openMonthly = () => navigate(`/monthly-user-view?month=${period.month}&year=${period.year}`);
  const openTeam = () =>
    navigate(ENABLE_MONTHLY_PM_VIEW_V2 ? `/monthly-people-mng-view-v2?year=${period.year}&month=${period.month}` : '/monthly-people-mng-view');

  if (!selectedUser) {
    return <Alert severity="info">Waiting for authentication to load...</Alert>;
  }

  const loading = !stats.ready;
  const missingColor = stats.missing ? DAY_COLORS.under : GREEN;

  return (
    <PageContainer>
      <PageHeader
        title={`${greeting(today)}${firstName ? `, ${firstName}` : ''}`}
        subtitle={today.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      >
        <MonthPicker year={period.year} month={period.month} onChange={(y, m) => setPeriod({ year: y, month: m })} />
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {stats.error && <Alert severity="error">{stats.error}</Alert>}

      <HomeHero stats={stats} monthName={monthName} period={when} onOpenDay={openDay} onOpenMonthly={openMonthly} />

      {/* Statistiche del mese */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<AccessTimeIcon />}
            color={GREEN}
            label="Hours tracked"
            value={formatHours(stats.total || 0)}
            unit="h"
            sub={`of ${formatHours(stats.monthTarget || 0)}h in ${MONTH_NAMES[period.month - 1]}`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<MissingIcon />}
            color={missingColor}
            label="Missing hours"
            value={formatHours(stats.missing || 0)}
            unit="h"
            sub={
              stats.incompleteDays?.length
                ? `${stats.incompleteDays.length} day${stats.incompleteDays.length === 1 ? '' : 's'} to complete`
                : when === 'future'
                  ? 'Month not started yet'
                  : 'Nothing missing'
            }
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<AverageIcon />}
            color="#3B82F6"
            label="Daily average"
            value={formatHours(stats.avgPerWorkingDay || 0)}
            unit="h"
            sub={stats.workingDaysToDate ? `over ${stats.workingDaysToDate} working days` : 'No working days yet'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            icon={<DaysOffIcon />}
            color="#E9A23B"
            label="Days off"
            value={stats.fullAbsenceDays || 0}
            unit={stats.fullAbsenceDays === 1 ? 'day' : 'days'}
            sub={
              stats.partialAbsenceDays
                ? `${stats.partialAbsenceDays} partial absence${stats.partialAbsenceDays === 1 ? '' : 's'}`
                : `No partial absences in ${MONTH_NAMES[period.month - 1]}`
            }
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Andamento + calendario */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <DailyHoursChart stats={stats} onOpenDay={openDay} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <MonthCalendar stats={stats} onOpenDay={openDay} />
        </Grid>
      </Grid>

      {/* Ripartizione + team */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={isPm ? 7 : 12}>
          <TimeBreakdown stats={stats} />
        </Grid>
        {isPm && (
          <Grid item xs={12} lg={5}>
            <TeamPulse team={team} onOpen={openTeam} />
          </Grid>
        )}
      </Grid>

      {/* Con la shell compatta guide e supporto sono nel menu utente */}
      {!ENABLE_COMPACT_SHELL && (
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 3, mt: 1 }}>
          {GUIDES.map((g) => (
            <Link key={g.url} href={g.url} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" sx={{ color: MUTED }}>
              {g.text}
            </Link>
          ))}
          <Link
            href={`mailto:${SUPPORT_CONFIG.email}?subject=Support Request - RUNtime App - [Brief description of the issue]`}
            underline="hover"
            variant="body2"
            sx={{ color: MUTED }}
          >
            Contact support
          </Link>
        </Box>
      )}
      <ClassicDesignNote />
      </Box>
    </PageContainer>
  );
}
